'use client'

import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation as useRouterLocation, useNavigate } from 'react-router-dom'
import { Divider } from '@vinted/web-ui'

import {
  GetShippingLabelContextProvider,
  DropOffPointMapContextProvider,
} from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import {
  useConversationContext,
  ConversationListContext,
  useConversationSidebarContext,
} from '@marketplace-web/messaging/conversation-providers-feature'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { scrollToTop } from '@marketplace-web/browser/browser-interaction-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import { ModeratedItemsHeader } from '@marketplace-web/moderation/conversation-moderation-feature'
import { MakeOffer, useGetSellerOfferOptions } from '@marketplace-web/offers/price-offers-feature'
import {
  ComposerType,
  INBOX_URL,
  conversationInfoIconClick,
  conversationUserUsernameClick,
  selfServiceViewScreenEvent,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { ConversationEducationModal } from '@marketplace-web/conversation-education/conversation-education-feature'
import {
  BlockedUserInfoMessage,
  PhishingFirstTimeListerEducation,
  PhishingReminderBanner,
} from '@marketplace-web/conversation-safety/conversation-safety-feature'
import { OfflineVerificationFeedback } from '@marketplace-web/conversation-feedback/conversation-feedback-feature'
import { HttpStatus } from '@marketplace-web/core-api/api-client-util'
import { useConversationOrderEmailTracking } from '@marketplace-web/order-emails/conversation-order-emails-tracking-feature'

import { useBrazeInboxMessageCards } from '@marketplace-web/braze/braze-feature'
import { transformBrazeInboxMessageCardDto } from '@marketplace-web/braze/braze-data'

import { useHandleConversationSSE } from '../../hooks/useHandleConversationSSE'

import Composer from '../Composer'
import ConversationContent from '../ConversationContent'
import ConversationHeader from '../ConversationHeader'
import HeroMessage from '../HeroMessage'
import LoadingFailedEmptyState from '../LoadingFailedEmptyState'
import NotFoundEmptyState from '../NotFoundEmptyState'
import ConversationLoader from '../ConversationLoader'
import ConversationDetails from '../ConversationDetails'
import { getConversationIdFromUrl, isBrazeConversation } from '../../utils'
import { useHandleConversationListSSE } from '../../hooks/useHandleConversationListSSE'

const Conversation = () => {
  const {
    conversation,
    transaction,
    conversationRequestStatus,
    conversationUiState,
    getConversationRequest,
    transactionUiState,
    setConversation,
    isFirstTimeListerEducationDialogVisible,
    setIsFirstTimeListerEducationDialogVisible,
    setConversationLoadStartForEscrowOrderABTest,
  } = useConversationContext()
  const { toggleSidebar, isSidebarOpen } = useConversationSidebarContext()
  const location = useRouterLocation()
  const conversationIdFromUrl = getConversationIdFromUrl(location.pathname)

  useConversationOrderEmailTracking({ transactionId: transaction?.id })

  const { inboxMessageCards } = useBrazeInboxMessageCards()

  const brazeConversationCard = useMemo(
    () => inboxMessageCards?.find(card => card.id === conversationIdFromUrl),
    [inboxMessageCards, conversationIdFromUrl],
  )

  const { track } = useTracking()
  const breakpoints = useBreakpoint()
  const navigate = useNavigate()
  const { offer_request } = useBrowserNavigation().searchParams

  const isReplyAllowed = !!(conversation?.isReplyAllowed || conversation?.userHasSupportRole)
  const userId = useSession().user?.id
  const displayedItem = conversation?.item

  const isBlockingImprovementsEnabled = useFeatureSwitch('conversation_blocking_improvements')
  const isNewConversationEducationVisible = useFeatureSwitch('web_new_conversation_education')
  const isWebMessagingSvcFSEnabled = useFeatureSwitch('web_messaging_svc')
  const { conversations, markAsRead, updateListConversationTitle } =
    useContext(ConversationListContext)
  const { sellerOfferOptions } = useGetSellerOfferOptions({
    transactionId: conversation?.transactionId,
    userSide: conversation?.transaction?.userSide,
  })

  const [showEducation, setShowEducation] = useState(false)
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(!!offer_request)

  useHandleConversationSSE()
  useHandleConversationListSSE()

  useEffect(() => {
    setShowEducation(!!conversation?.education)
  }, [conversation])

  const conversationInList = conversations.find(conv => conversation?.id === conv.id)

  const isLoading =
    !conversation &&
    (!inboxMessageCards || [UiState.Pending, UiState.Idle].includes(conversationUiState))
  const isBraze = !isLoading && !!brazeConversationCard
  const isError = !isLoading && !conversation && conversationUiState === UiState.Failure
  const isConversationNotFound = isError && conversationRequestStatus === HttpStatus.NotFound

  const isPhishingReminderBannerVisible =
    isReplyAllowed && !isBraze && !!conversation?.transaction?.userSide

  useEffect(() => {
    if (!isLoading) return

    setConversationLoadStartForEscrowOrderABTest(new Date().getTime())
  }, [setConversationLoadStartForEscrowOrderABTest, isLoading])

  useEffect(() => {
    if (
      !userId ||
      brazeConversationCard ||
      !inboxMessageCards ||
      conversationUiState !== UiState.Idle
    ) {
      return
    }

    const isConversationIdMismatched = String(conversation?.id) !== conversationIdFromUrl

    if (!isConversationIdMismatched) return

    getConversationRequest({ conversationId: conversationIdFromUrl })
  }, [
    getConversationRequest,
    conversation,
    conversationIdFromUrl,
    conversationUiState,
    userId,
    location,
    brazeConversationCard,
    inboxMessageCards,
  ])

  useEffect(() => {
    if (!conversation) return

    if (!conversationInList || isBrazeConversation(conversationInList)) return

    if ('unread' in conversationInList && !conversationInList.unread) return

    markAsRead({ id: conversation.id })
  }, [conversationInList, conversation, markAsRead])

  useEffect(() => {
    if (!breakpoints.portables) return

    scrollToTop()
  }, [breakpoints])

  useEffect(() => {
    if (!brazeConversationCard || conversation?.id === conversationIdFromUrl) return

    const brazeConversation = transformBrazeInboxMessageCardDto(brazeConversationCard)

    setConversation(brazeConversation)
  }, [brazeConversationCard, setConversation, conversationIdFromUrl, conversation?.id])

  const trackViewScreen = useCallback(
    (screen: string) => {
      if (!conversation?.transactionId) return

      const event = selfServiceViewScreenEvent({
        screen,
        transactionId: conversation.transactionId,
      })

      track(event)
    },
    [track, conversation?.transactionId],
  )

  useEffect(() => {
    trackViewScreen('message_reply')
  }, [trackViewScreen])

  if (isConversationNotFound) {
    return <NotFoundEmptyState />
  }

  if (isError) {
    return <LoadingFailedEmptyState />
  }

  if (isLoading) {
    return <ContentLoader testId="conversation-page-loader" />
  }

  if (
    conversation?.transactionId &&
    transactionUiState !== UiState.Success &&
    !isWebMessagingSvcFSEnabled
  ) {
    return <ContentLoader testId="conversation-page-loader" />
  }

  const handleToggleSidebar = () => {
    toggleSidebar()

    trackViewScreen('order_details')
  }

  const handleInfoIconClick = () => {
    if (conversation?.transactionId && conversation?.id)
      track(
        conversationInfoIconClick({
          transactionId: conversation?.transactionId,
          conversationId: String(conversation.id),
          userSide: conversation.transaction?.userSide ?? null,
          oppositeUserId: conversation.oppositeUser?.id ?? null,
        }),
      )

    handleToggleSidebar()
  }

  const handleGoBack = () => {
    navigate(INBOX_URL)
  }

  const closeConversationEducationModal = () => {
    setShowEducation(false)
  }

  const openOfferModal = () => {
    setIsOfferModalOpen(true)
  }

  const renderPhishingReminderBanner = () => {
    if (!isPhishingReminderBannerVisible || !conversation?.id) return null

    return <PhishingReminderBanner threadId={String(conversation.id)} />
  }

  const renderHeroMessage = () => {
    if (
      !conversation?.transaction?.order ||
      !conversation?.transaction?.offer ||
      !conversation?.transaction?.itemsCount
    ) {
      return null
    }

    return (
      <>
        <HeroMessage toggleSidebar={handleToggleSidebar} openOfferModal={openOfferModal} />
        <Divider />
      </>
    )
  }

  const renderModeratedItemHeader = () => {
    if (!conversation?.moderatedItems?.itemCount) return null

    return (
      <ModeratedItemsHeader
        moderatedItems={conversation.moderatedItems}
        toggleSidebar={handleToggleSidebar}
      />
    )
  }

  const renderConversationEducationModal = () => {
    if (!isNewConversationEducationVisible || !conversation?.education) return null

    return (
      <ConversationEducationModal
        conversation={conversation}
        isOpen={showEducation}
        onClose={closeConversationEducationModal}
      />
    )
  }

  const renderBlockedUserIfoMessage = () => {
    if (!conversation?.oppositeUser?.isHated || !isBlockingImprovementsEnabled) return null

    return (
      <>
        <Divider />
        <BlockedUserInfoMessage
          oppositeUserId={conversation.oppositeUser.id}
          userLogin={conversation.oppositeUser.login}
        />
      </>
    )
  }

  const renderMakeOfferModal = () => {
    if (!displayedItem) return null

    return (
      <MakeOffer
        isOpen={isOfferModalOpen}
        onClose={() => setIsOfferModalOpen(false)}
        itemId={displayedItem.id}
        itemTitle={conversation?.transaction?.order.title}
        itemThumbnailUrl={transaction?.order.items[0]?.thumbnailUrl}
        sellerOfferOptions={sellerOfferOptions}
        sellerId={conversation?.transaction?.seller?.id}
      />
    )
  }

  const handleUsernameClick = () => {
    if (!conversation?.id || !conversation?.transactionId) return

    track(
      conversationUserUsernameClick({
        conversationId: String(conversation?.id),
        transactionId: conversation?.transactionId,
        userSide: conversation.transaction?.userSide ?? null,
        oppositeUserId: conversation.oppositeUser?.id ?? null,
      }),
    )
  }

  const showMessagesContainer = breakpoints.tabletsUp || (breakpoints.portables && !isSidebarOpen)

  return (
    <GetShippingLabelContextProvider>
      <DropOffPointMapContextProvider shipmentId={conversation?.transaction?.shipment?.id}>
        <div className="thread__wrapper">
          {showMessagesContainer && (
            <div className="thread__messages-container">
              <ConversationHeader
                oppositeUser={conversation?.oppositeUser}
                onInfoIconClick={handleInfoIconClick}
                onGoBack={handleGoBack}
                onUsernameClick={handleUsernameClick}
                isSystem={isBraze}
              />
              <ConversationLoader />
              <Divider />
              {renderHeroMessage()}
              {renderModeratedItemHeader()}
              <div className="thread__messages-wrapper">
                <div className="thread__messages-main">
                  <ConversationContent
                    toggleSidebar={handleToggleSidebar}
                    openOfferModal={openOfferModal}
                  />
                </div>
              </div>
              {renderPhishingReminderBanner()}
              {isReplyAllowed && !isBraze && (
                <Composer type={ComposerType.Reply} onPostReply={updateListConversationTitle} />
              )}
              {renderBlockedUserIfoMessage()}
              {renderConversationEducationModal()}
            </div>
          )}
          {isSidebarOpen && (
            <div
              className="thread-sidebar__container thread-sidebar__container--reworked"
              data-testid="sidebar"
            >
              <ConversationDetails toggleSidebar={handleToggleSidebar} />
            </div>
          )}
          <PhishingFirstTimeListerEducation
            isOpen={isFirstTimeListerEducationDialogVisible}
            setIsOpen={setIsFirstTimeListerEducationDialogVisible}
          />
          <OfflineVerificationFeedback />
          {renderMakeOfferModal()}
        </div>
      </DropOffPointMapContextProvider>
    </GetShippingLabelContextProvider>
  )
}

export default Conversation
